import {
  ShareScreenBase,
  SKIP_WAITLIST_TITLE,
  skipWaitlistSuccessMessage,
} from "./share-screen-base"
import { RoomContentCard } from "./room-content-card"
import { getRoomDeeplinkUrl } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { brandAppName } from "~/common/brand"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import { getInviterData, getRoomData, getTopMessages } from "~/share/share-embedded-data"
import { useRoomSharePhoneSubmission } from "~/hooks/use-phone-submission"
import { useRoomShareEmailSubmission } from "~/hooks/use-email-submission"
import { useCollectEmail } from "~/hooks/use-collect-email"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS, SHARE_PAGES } from "~/common/analytics"
import { useScreenTracking } from "~/hooks/use-screen-tracking"
import { TopMessagesSection } from "./components/top-messages-section"
import { PresentedBy } from "./components/room-content-card/presented-by"

export const ShareRoomScreen = (): JSX.Element => {
  const roomData = getRoomData()
  const topMessages = getTopMessages()
  const collectEmail = useCollectEmail()

  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getRoomDeeplinkUrl(appEnvironment, roomData.id)
  const roomTitle = [roomData.name, `on ${brandAppName()}`].join(" ")
  useDocumentTitleDescription(roomTitle, roomData.description ?? null)

  const phoneSubmission = useRoomSharePhoneSubmission()
  const emailSubmission = useRoomShareEmailSubmission()

  const roomId = roomData.id
  const roomHandle = roomData.roomHandle ?? undefined

  useScreenTracking(SCREENS.Share, {
    share_page: SHARE_PAGES.Room,
    room_id: roomId,
    room_handle: roomHandle,
    deeplink_url: deeplinkUrl,
    ad_id: roomData.sponsorCreative?.id,
  })

  const onJoinPress = useCallback(async () => {
    const successMessage = skipWaitlistSuccessMessage(collectEmail)
    const successTitle = SKIP_WAITLIST_TITLE
    const inviterData = getInviterData()

    const trackingData = {
      unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
      share_page: SHARE_PAGES.Room,
      inviter_id: inviterData?.id,
      inviter_username: inviterData?.username,
      room_id: roomId,
      deeplink_url: deeplinkUrl,
      room_handle: roomHandle,
    }

    if (collectEmail) {
      await emailSubmission.submitEmail({
        successMessage,
        successTitle,
        roomId,
        trackingData,
      })
    } else {
      await phoneSubmission.submitPhoneNumber({
        successMessage,
        successTitle,
        roomId,
        trackingData,
      })
    }
  }, [collectEmail, emailSubmission, phoneSubmission, deeplinkUrl, roomHandle, roomId])

  const inputProps = collectEmail
    ? {
        inputMode: "email" as const,
        emailValue: emailSubmission.emailValue,
        setEmailValue: emailSubmission.setEmailValue,
      }
    : {
        inputMode: "phone" as const,
        phoneValue: phoneSubmission.phoneValue,
        setPhoneValue: phoneSubmission.setPhoneValue,
      }

  return (
    <ShareScreenBase
      deeplinkUrl={deeplinkUrl}
      onJoinPress={onJoinPress}
      analyticsProps={{
        room_id: roomId,
        room_handle: roomHandle,
      }}
      {...inputProps}
      renderContent={() =>
        roomData && (
          <div className="flex w-full max-w-[640px] flex-col">
            <RoomContentCard roomData={roomData} compact={!!topMessages} />
            <PresentedBy roomData={roomData} />
            {topMessages && <TopMessagesSection messages={topMessages} />}
          </div>
        )
      }
    />
  )
}

export default ShareRoomScreen
